<!--
  Fix dynamic password icon (store to config)
-->
<template>
  <form
    ref="register"
    @keyup.enter="submit()"
    v-on:submit.prevent=""
  >
    <h1>Register</h1>

    <div class="row row--separator row--separatorTop">
      <div class="row__full">
        <p v-if="errorMessage" class="row__error" v-html="errorMessage" />
        <p v-else class="row__notice">If you already have an account, please <router-link to="/login" class="link">login</router-link>.</p>
      </div>
    </div>

    <div class="row row--separator">
      <div class="row__full">
        <FormInput
          :value="email"
          ref-name="email"
          placeholder="you@company.com"
          label="Email"
          alt-label="Use your company/organization email"
          type="email"
          class="form__input"
          autocomplete="email"
          iconSize="md"
          :icon="icon.email"
          :error="errors.email"
          :status="status.email"
          @inputChanged="setFieldValue($event, 'email')"
          @unfocused="validate('email')"
        />
      </div>
    </div>

    <div class="row">
      <div class="row__full">
        <FormInput
          :value="password"
          ref-name="password"
          placeholder="*********"
          label="Password"
          alt-label="Minimum of 8 characters"
          class="form__input"
          type="password"
          autocomplete="new-password"
          iconSize="md"
          :icon="icon.password[0]"
          :error="errors.password"
          :status="status.password"
          :strength="passwordStrength"
          @change="checkPasswordStrength"
          @inputChanged="setFieldValue($event, 'password')"
          @unfocused="validate('password')"
        />
      </div>
    </div>

    <div class="row">
      <div class="row__full">
        <FormInput
          :value="firstName"
          ref-name="firstName"
          placeholder="John"
          label="First name"
          alt-label="Please fill in your first name"
          type="text"
          class="form__input"
          autocomplete="given-name"
          iconSize="md"
          :icon="icon.firstName"
          :error="errors.firstName"
          :status="status.firstName"
          @inputChanged="setFieldValue($event, 'firstName')"
          @unfocused="validate('firstName')"
        />
      </div>
    </div>

    <div class="row">
      <div class="row__full">
        <FormInput
          :value="lastName"
          ref-name="lastName"
          placeholder="Doe"
          label="Last name"
          alt-label="Please fill in your last name"
          type="text"
          class="form__input"
          autocomplete="family-name"
          iconSize="md"
          :icon="icon.lastName"
          :error="errors.lastName"
          :status="status.lastName"
          @inputChanged="setFieldValue($event, 'lastName')"
          @unfocused="validate('lastName')"
        />
      </div>
    </div>

    <div class="row">
      <div class="row__full">
        <FormInput
          :value="referralCode"
          ref-name="referralCode"
          placeholder="Referral code"
          label="Referral code"
          alt-label="Fill in if you have referral code"
          type="text"
          class="form__input"
          iconSize="md"
          :icon="icon.referralCode"
          :error="errors.referralCode"
          :status="status.referralCode"
          @inputChanged="setFieldValue($event, 'referralCode')"
          @unfocused="validate('referralCode')"
        />
      </div>
    </div>

    <div class="row row--newsletter">
      <div class="row__left">
        <FormSwitch
          v-model="newsletter"
          class="form__switch"
          style="top:50%; transform:translate(0,-50%);"
          :error="errors.newsletter"
        />
      </div>

      <div class="row__right">
        <p class="row__notice">
          I want to <span class="highlight">subscribe to Statzon's monthly newsletter</span> to receive the latest insights and updates.
        </p>
      </div>
    </div>

    <div class="row row--separator">
      <div class="row__full">
        <FormSubmit
          :status="formStatus"
          icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm7.753 18.305c-.261-.586-.789-.991-1.871-1.241-2.293-.529-4.428-.993-3.393-2.945 3.145-5.942.833-9.119-2.489-9.119-3.388 0-5.644 3.299-2.489 9.119 1.066 1.964-1.148 2.427-3.393 2.945-1.084.25-1.608.658-1.867 1.246-1.405-1.723-2.251-3.919-2.251-6.31 0-5.514 4.486-10 10-10s10 4.486 10 10c0 2.389-.845 4.583-2.247 6.305z"/></svg>'
          @click="submit()"
        >
          {{ formStatus === 'success' ? 'Success' : 'Create account' }}
        </FormSubmit>
      </div>
    </div>

    <div class="row">
      <div class="row__full">
        <div class="divider">
          <div class="divider__left" />
          <div class="divider__center">
            <p>Or sign up with</p>
          </div>
          <div class="divider__right" />
        </div>

        <div class="social">
          <UtilityCtaButton
            icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>'
            :noEffect="true"
            textTransform="none"
            color="blank"
            @click="socialLogin('linkedin-openid')"
          >
            LinkedIn
          </UtilityCtaButton>

          <UtilityCtaButton
            icon='<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/><path d="M1 1h22v22H1z" fill="none"/></svg>'
            :noEffect="true"
            textTransform="none"
            color="blank"
            @click="socialLogin('google')"
          >
            Google
          </UtilityCtaButton>
        </div>
      </div>
    </div>

    <div class="row row--separator">
      <div class="row__full">
        <p class="row__notice">
          You agree to our <a href="https://statzon.com/terms-and-conditions" class="link" target="_blank">terms of service</a> by signing up.
        </p>
      </div>
    </div>
  </form>
</template>

<script>
import FormInput from '@/components/form/Input.vue'
import FormSwitch from '@/components/form/Switch.vue'
import FormSubmit from '@/components/form/Submit.vue'
import UtilityCtaButton from '@/components/utility/CtaButton.vue'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    FormInput,
    FormSwitch,
    FormSubmit,
    UtilityCtaButton
  },
  data () {
    return {
      form: false,
      formStatus: 'default',
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      countryCode: '',
      referralCode: '',
      newsletter: false,
      passwordStrength: 'none',
      errorMessage: '',
      errors: {
        email: false,
        password: false,
        firstName: false,
        lastName: false,
        referralCode: false
      },
      status: {
        email: false,
        password: false,
        firstName: false,
        lastName: false,
        referralCode: false
      },
      icon: {
        email: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 12.713l-11.985-9.713h23.971l-11.986 9.713zm-5.425-1.822l-6.575-5.329v12.501l6.575-7.172zm10.85 0l6.575 7.172v-12.501l-6.575 5.329zm-1.557 1.261l-3.868 3.135-3.868-3.135-8.11 8.848h23.956l-8.11-8.848z"/></svg>',
        firstName: '<svg width="100%" height="100%" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M24 24h-24v-24h24v24zm-2-22h-20v20h20v-20zm-4.118 14.064c-2.293-.529-4.427-.993-3.394-2.945 3.146-5.942.834-9.119-2.488-9.119-3.388 0-5.643 3.299-2.488 9.119 1.064 1.963-1.15 2.427-3.394 2.945-2.048.473-2.124 1.49-2.118 3.269l.004.667h15.993l.003-.646c.007-1.792-.062-2.815-2.118-3.29z"/></svg>',
        lastName: '<svg width="100%" height="100%" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M24 0v24h-24v-24h24zm-6.118 16.064c-2.293-.529-4.427-.993-3.394-2.945 3.146-5.942.834-9.119-2.488-9.119-3.388 0-5.643 3.299-2.488 9.119 1.064 1.963-1.15 2.427-3.394 2.945-2.048.473-2.124 1.49-2.118 3.269l.004.667h15.993l.003-.646c.007-1.792-.062-2.815-2.118-3.29z"/></svg>',
        password: [
          '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-5 7.723v2.277h-2v-2.277c-.595-.347-1-.984-1-1.723 0-1.104.896-2 2-2s2 .896 2 2c0 .738-.404 1.376-1 1.723zm-5-7.723v-4c0-2.206 1.794-4 4-4 2.205 0 4 1.794 4 4v4h-8z"/></svg>',
          '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10-4c0-2.206 1.794-4 4-4 2.205 0 4 1.794 4 4v4h-8v-4zm3.408 14l-2.842-2.756 1.172-1.173 1.67 1.583 3.564-3.654 1.174 1.173-4.738 4.827z"/></svg>',
          '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-4.138 9.975l-1.862-1.836-1.835 1.861-1.13-1.129 1.827-1.86-1.862-1.837 1.129-1.13 1.859 1.827 1.838-1.871 1.139 1.139-1.833 1.86 1.868 1.836-1.138 1.14zm-5.862-9.975v-4c0-2.206 1.795-4 4-4s4 1.794 4 4v4h-8z"/></svg>'
        ],
        referralCode: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M10.605 0h-10.605v10.609l13.391 13.391 10.609-10.604-13.395-13.396zm-7.02 6.415c-.781-.783-.781-2.048 0-2.829.782-.782 2.048-.781 2.829-.001.782.783.781 2.047 0 2.829-.781.781-2.046.781-2.829.001zm3.101 4.514l4.243-4.243.707.707-4.243 4.243-.707-.707zm1.414 1.414l4.243-4.243.354.354-4.243 4.243-.354-.354zm1.061 1.061l4.243-4.243 1.061 1.061-4.243 4.243-1.061-1.061zm1.768 1.768l4.243-4.243.354.354-4.243 4.243-.354-.354zm1.767 1.767l-.707-.707 4.243-4.243.707.707-4.243 4.243zm.708.707l4.242-4.242.354.354-4.242 4.242-.354-.354z"/></svg>'
      }
    }
  },
  mounted () {
    window.addEventListener('message', this.onMessage, false)
  },
  beforeDestroy () {
    window.removeEventListener('message', this.onMessage)
  },
  methods: {
    async submit () {
      this.errorMessage = ''

      if (this.validate('all')) {
        this.formStatus = 'loading'

        let formData = {
          hubspotutk: this.getCookie('hubspotutk'),
          email: this.email,
          password: this.password,
          first_name: this.firstName,
          last_name: this.lastName,
          referral_code: this.referralCode.length > 0 ? this.referralCode : '',
          newsletter: this.newsletter,
          step: 'a'
        }

        try {
          let response = await this.$store.dispatch('user/register', formData)

          if (response.data.status === 'ok') {
            this.formStatus = 'success'
            this.$store.dispatch('app/setVerificationHash', response.data.attributes.verification_hash)
            this.$store.dispatch('app/setRegistrationEmail', response.data.attributes.email)
            this.$emit('registered')
          }
        } catch (error) {
          if (error.data) {
            if (error.data.errors) {
              this.errors = error.data.errors
              error.data.errors.forEach(err => {
                this.errorMessage += err.title + '<br>'
              })
            }
          }

          this.formStatus = 'error'
        }
      }
    },
    async socialLogin (some) {
      const newWindow = this.openWindow('', 'message')
      this.$store.dispatch('user/socialLoginRedirect', some)
        .then(response => {
          newWindow.location.href = response.data.data.url
          this.sendGoogleAnalyticsEvent('auth_pre_social_register', some, '', this.$store.getters['user/getSubscriptionStatus'])
        })
        .catch(function (error) {
          console.error(error);
        })
    },
    async onMessage (e) {
      if (e.data.token && e.data.mode && e.data.token.length > 10) {
        this.formStatus = 'loading'
        let formData = {
          token: e.data.token,
          mode: e.data.mode
        }

        try {
          let response = await this.$store.dispatch('user/login', formData)
          this.username = response.data.attributes.first_name
          this.formStatus = 'success'
          this.$emit('registered')
          this.$emit('loggedIn', true)
          this.sendGoogleAnalyticsEvent('auth_social_register', e.data.mode, '', this.$store.getters['user/getSubscriptionStatus'])
        } catch (error) {
          if (error.data) {
            if (error.data.errors.title) { this.errorMessage = error.data.errors.title }
          }
          this.formStatus = 'error'
        }
      } else {
        console.log({error: 'token missing', e})
      }
    },
    openWindow (url, title, options = {}) {
      if (typeof url === 'object') {
        options = url
        url = ''
      }

      options = { url, title, width: 600, height: 720, ...options }

      const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screen.left
      const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screen.top
      const width = window.innerWidth || document.documentElement.clientWidth || window.screen.width
      const height = window.innerHeight || document.documentElement.clientHeight || window.screen.height

      options.left = ((width / 2) - (options.width / 2)) + dualScreenLeft
      options.top = ((height / 2) - (options.height / 2)) + dualScreenTop

      const optionsStr = Object.keys(options).reduce((acc, key) => {
        acc.push(`${key}=${options[key]}`)
        return acc
      }, []).join(',')

      const newWindow = window.open(url, title, optionsStr)

      if (window.focus) {
        newWindow.focus()
      }

      return newWindow
    },
    validate (field) {
      let valid = true

      if (field === 'email' || field === 'all') {
        if (this.email === '') {
          this.errors.email = 'Required field'
          valid = false
        } else if (this.email.length > 255) {
          this.errors.email = 'Maximum of 255 characters'
          valid = false
        } else if (this.email !== '') {
          let emailTest = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          if (!emailTest.test(this.email)) {
            this.errors.email = 'Email must be valid'
            valid = false
          } else {
            this.errors.email = undefined
          }
        } else {
          this.errors.email = undefined
        }

        this.status.email = this.errors.email ? 'invalid' : 'valid'
      }

      if (field === 'password' || field === 'all') {
        if (this.password === '') {
          this.errors.password = 'Required field'
          valid = false
        } else if (this.password.length < 8) {
          this.errors.password = 'Minimum of 8 characters'
          valid = false
        } else {
          this.errors.password = undefined
        }

        this.status.password = this.errors.password ? 'invalid' : 'valid'
      }

      if (field === 'firstName' || field === 'all') {
        if (this.firstName === '') {
          this.errors.firstName = 'Required field'
          valid = false
        } else if (this.firstName.length > 64) {
          this.errors.firstName = 'Maximum of 64 characters'
          valid = false
        } else {
          this.errors.firstName = undefined
        }

        this.status.firstName = this.errors.firstName ? 'invalid' : 'valid'
      }

      if (field === 'lastName' || field === 'all') {
        if (this.lastName === '') {
          this.errors.lastName = 'Required field'
          valid = false
        } else if (this.lastName.length > 64) {
          this.errors.lastName = 'Maximum of 64 characters'
          valid = false
        } else {
          this.errors.lastName = undefined
        }

        this.status.lastName = this.errors.lastName ? 'invalid' : 'valid'
      }

      if (field === 'referralCode' || field === 'all') {
        if (this.referralCode.length > 64) {
          this.errors.referralCode = 'Maximum of 64 characters'
          valid = false
        } else {
          this.errors.referralCode = undefined
        }

        this.status.referralCode = this.errors.referralCode ? 'invalid' : 'valid'
      }

      return valid
    },
    checkPasswordStrength () {
      if (this.password.length === 0) {
        this.passwordStrength = 'none'
      } else if (this.password.length >= 8) {
        if (/[A-Z]/.test(this.password) && /\d/.test(this.password)) {
          this.passwordStrength = 'strong'
        } else {
          this.passwordStrength = 'good'
        }
      } else {
        this.passwordStrength = 'weak'
      }
    },
    setFieldValue (value, field) {
      this[field] = value
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

h1, h2 {
  @include font('header--xl', $white, 0.9);
  width: 90%;
  margin: 0 auto;
  text-align: center;
  text-transform: uppercase;

  @media screen and (min-width: $breakpointLg) {
    @include font('header--xxl', $white, 0.9);
    margin: 0 auto;
  }
}

p {
  @include font('content--md', $white);

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white);
  }
}

.highlight {
  border-bottom: 1px dotted $white;
  color: $white !important;
}

.divider {
  display: flex;
  align-items: center;

  p {
    @include font('content--xs', $white, 0.7);
    text-transform: uppercase;

    @media screen and (min-width: $breakpointLg) {
      @include font('content--sm', $white, 0.7);
    }
  }

  &__left, &__middle, &__right {
    flex: 1;
  }

  &__left {
    background-color: rgba($white, 0.07);
    height: 1px;
    margin-right: 1rem;
  }

  &__right {
    background-color: rgba($white, 0.07);
    height: 1px;
    margin-left: 1rem;
  }
}

.social {
  margin-top: 0.5rem;

  button {
    margin: 0 0.5rem;
  }
}

</style>
